import { useState, useEffect, useMemo, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { Accordion } from "@/components/ui/Accordion";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { JobService } from "@/services/jobService";
import CategorySection from "./CategorySection";
import RetryButton from "@/components/shared/RetryButton";

/**
 * Both option lists come from `GET /job/field-config`, and both arrive empty
 * when the tenant is not entitled to the module behind them. An empty list is
 * the whole gate: the section does not render, so nothing is selected, so the
 * server never has to refuse the job payload.
 *
 * No second fetch, and no hardcoded platform table. The server owns every
 * label — see `app/config/enum_options.py`.
 */
const FieldConfigSelector = ({ formMethods, jobId , disabled=false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fieldConfig, setFieldConfig] = useState(null);
  const [interviewTypes, setInterviewTypes] = useState([]);
  const [booleanPlatforms, setBooleanPlatforms] = useState([]);
  const [openAccordions, setOpenAccordions] = useState([]);
  // Keys the JOB decides, with the sentence saying what decides them. Both come
  // from GET /job/field-config so the screen and the server cannot disagree
  // about which fields a recruiter may tick.
  const [derivedKeys, setDerivedKeys] = useState([]);
  const [derivedNote, setDerivedNote] = useState("");
  // Which field on a Step 1 language row drives which of those keys. Sent as a
  // map rather than inferred from `derivedKeys` order, because the two are not
  // one decision — see the effect below.
  const [languageFieldKeys, setLanguageFieldKeys] = useState({});

  const { setValue, watch, getValues } = formMethods;
  const mandatoryFields = watch("mandatoryFields") || [];
const selectedInterviewTypes = watch("selectedInterviewTypes") ?? [];
const selectedBooleanPlatforms = watch("selectedBooleanPlatforms") ?? [];
  const mandatoryFieldSet = useMemo(() => new Set(mandatoryFields), [mandatoryFields]);
  const derivedKeySet = useMemo(() => new Set(derivedKeys), [derivedKeys]);

  // Step 1's Language & Proficiency rows, watched live.
  const languagesRequired = watch("languagesRequired");

  // Each key asks its own question of those rows, because the two are not one
  // decision: a job asking for "Spanish, any level" has named a language and no
  // level, and making proficiency mandatory there would refuse a candidate who
  // reported exactly what the job asked for. Name a level on any row and that
  // second key comes on; clear it and it goes off again, without costing the
  // language its own.
  //
  // The server recomputes exactly this on every write (mandatory_keys_for_job),
  // so the mirror here is not the rule — it is what keeps the screen and the
  // payload honest about what the server is going to store anyway.
  useEffect(() => {
    const drivers = Object.entries(languageFieldKeys);
    if (!drivers.length) return;

    const rows = languagesRequired ?? [];
    const current = getValues("mandatoryFields") || [];
    const next = new Set(current);

    drivers.forEach(([rowField, key]) => {
      if (rows.some((row) => row?.[rowField]?.trim())) next.add(key);
      else next.delete(key);
    });

    const updated = [...next];
    if (updated.length !== current.length || updated.some((key, i) => key !== current[i])) {
      setValue("mandatoryFields", updated, { shouldValidate: true });
    }
  }, [languagesRequired, languageFieldKeys]);
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // A plain `await`, not `Promise.allSettled`. `allSettled` never rejects,
      // so the `catch` below could not run and `setError` was unreachable: a
      // failed `GET /job/field-config` left `fieldConfig` null, `loading` false,
      // and rendered step 2 of the job form as an empty panel with a live
      // Create button under it. The error branch and its Retry button existed
      // the whole time; nothing could reach them.
      const fieldResponse = await JobService.fetchFieldConfig();

      // ── Field Config ──
      if (!fieldResponse?.success) {
        throw new Error(fieldResponse?.message || "Failed to load configuration");
      }

      const data = fieldResponse.data;

      setFieldConfig(data);
      setDerivedKeys(data.derived_keys ?? []);
      setDerivedNote(data.derived_note ?? "");
      setLanguageFieldKeys(data.language_field_keys ?? {});

      const configurableKeys = (data.configurable || []).map((f) => f.key);

      for (const field of ["mandatoryFields", "selectedConfigurable"]) {
        const current = getValues(field) || [];

        const filtered = current.filter((k) => configurableKeys.includes(k));

        if (filtered.length !== current.length) {
          setValue(field, filtered, {
            shouldValidate: false,
          });
        }
      }

      const categories = [...new Set(data.configurable?.map((f) => f.category).filter(Boolean))];

      if (categories.length > 0) {
        setOpenAccordions([categories[0]]);
      }

      // ── The two entitlement-gated option lists ──
      //
      // A non-empty list is filtered against the options actually offered,
      // so a type retired from the enum cannot be re-submitted.
      //
      // **An empty list is not a filter — it is "no section".** The server
      // sends `[]` when the tenant is not entitled, and this used to filter
      // the stored selection against it, writing `[]` into the form. The
      // form then submitted `[]`, and a job that had six question types kept
      // none; turning the module back on did not bring them back.
      //
      // So the field is cleared to `undefined` instead, and `undefined` is
      // omitted from the payload. "I am not telling you about this field" is
      // the true statement, and it is the only one that preserves what is
      // stored. Submitting the stored values instead would be worse still:
      // the server refuses a non-empty selection from an unentitled tenant,
      // so every job edit would fail with a 403.
      const types = data.interview_question_types ?? [];
      const platforms = data.boolean_platforms ?? [];

      setInterviewTypes(types);
      setBooleanPlatforms(platforms);

      for (const [field, options] of [
        ["selectedInterviewTypes", types],
        ["selectedBooleanPlatforms", platforms],
      ]) {
        if (options.length === 0) {
          if (getValues(field) !== undefined) {
            setValue(field, undefined, { shouldValidate: false });
          }
          continue;
        }

        const validValues = options.map((o) => o.value);
        const current = getValues(field) || [];
        const filtered = current.filter((v) => validValues.includes(v));

        if (filtered.length !== current.length) {
          setValue(field, filtered, { shouldValidate: false });
        }
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to load configuration");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggle = (key) => {
    const current = getValues("mandatoryFields") || [];
    const updated = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
    setValue("mandatoryFields", updated, { shouldValidate: true });
  };

  const handleBulk = (keys, add) => {
    const current = getValues("mandatoryFields") || [];
    const updated = add ? [...new Set([...current, ...keys])] : current.filter((k) => !keys.includes(k));
    setValue("mandatoryFields", updated, { shouldValidate: true });
  };

  const handleTypeToggle = (id) => {
    const current = getValues("selectedInterviewTypes") || [];
    const updated = current.includes(id) ? current.filter((k) => k !== id) : [...current, id];
    setValue("selectedInterviewTypes", updated, { shouldValidate: true });
  };

  const handleTypeBulk = (add) => {
    const updated = add ? interviewTypes.map((t) => t.value) : [];
    setValue("selectedInterviewTypes", updated, { shouldValidate: true });
  };

  const handlePlatformToggle = (value) => {
    const current = getValues("selectedBooleanPlatforms") || [];
    const updated = current.includes(value) ? current.filter((k) => k !== value) : [...current, value];
    setValue("selectedBooleanPlatforms", updated, { shouldValidate: true });
  };

  const handlePlatformBulk = (add) => {
    const updated = add ? booleanPlatforms.map((p) => p.value) : [];
    setValue("selectedBooleanPlatforms", updated, { shouldValidate: true });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-400 mb-4">{error}</p>
        <RetryButton onClick={loadData} className="px-4 py-2 text-sm" />
      </div>
    );
  }

  if (!fieldConfig) return null;

  const { always_mandatory: alwaysMandatory = [], configurable = [] } = fieldConfig;

  // Grouped by category, and each group carries the title the server sent with
  // it. Insertion order is the order the API listed the fields in, which is the
  // order the accordions appear in.
  //
  // Title priority: the server's `category_label` first; without one, the
  // title-cased key — the same rule the server's `category_label()` uses — so
  // a payload missing labels can never render every accordion as "Other".
  const titleOf = (field) => {
    if (field.category_label) return field.category_label;
    const key = String(field.category || "").split(".").pop().replace(/_/g, " ").trim();
    if (!key) return "Other";
    return key.replace(/\b\w/g, (c) => c.toUpperCase());
  };
  const grouped = configurable.reduce((acc, field) => {
    const cat = field.category || "Other";
    (acc[cat] ??= { label: titleOf(field), fields: [] }).fields.push(field);
    return acc;
  }, {});

  return (
    <div className="space-y-8">
      {/* ── Section 1: Required Fields ──── */}
      {alwaysMandatory.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[16px] font-semibold text-text-primary">Field Configuration</h3>
          </div>
          <p className="text-sm text-text-muted mb-4">Select the fields that a candidate must fill in while applying for this job.</p>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-text-primary">Required Fields</h4>
              <span className="text-sm text-text-muted">Always mandatory</span>
            </div>
            <div className="space-y-2 bg-background-primary/30 rounded-lg p-3 border border-border-primary/50">
              {alwaysMandatory.map((field) => (
                <label key={field.key} className="flex items-center gap-3 py-1.5 cursor-not-allowed">
                  <Input type="checkbox" checked disabled={disabled} className="h-4 w-4 rounded border-border-primary bg-background-form text-button-secondary cursor-not-allowed" />
                  <span className="text-sm text-text-primary">{field.label}</span>
                  <span className="ml-auto text-xs text-text-primary bg-background-invert/10 px-2 py-0.5 rounded">Mandatory</span>
                </label>
              ))}
            </div>
          </div>

          {/* ── Section 2: Additional (configurable) Fields ───*/}
          {configurable.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-text-primary mb-3">Additional Fields</h4>
              <Accordion value={openAccordions} onValueChange={setOpenAccordions} multiple>
                {Object.entries(grouped).map(([category, { label, fields }]) => (
                  <CategorySection disabled={disabled} key={category} category={category} label={label} fields={fields} mandatoryFieldSet={mandatoryFieldSet} derivedKeys={derivedKeySet} derivedNote={derivedNote} onToggle={handleToggle} onBulk={handleBulk} />
                ))}
              </Accordion>
            </div>
          )}
        </div>
      )}

      {/* ── Section 3: Interview Question Types ─── */}
      {interviewTypes.length > 0 && (
        <div>
          <h3 className="text-[16px] font-semibold text-text-primary mb-1">Interview Question Types</h3>
          <p className="text-sm text-text-muted mb-4">Select the interview question types to generate for this job.</p>

          <div className="bg-background-primary/30 rounded-lg p-3 border border-border-primary/50">
            <div className="grid grid-cols-2 gap-x-4 gap-y-0">
  {interviewTypes.map((type) => {
    const isSelected = selectedInterviewTypes.includes(type.value);
    return (
      <label
        key={type.value}
        className="flex items-center gap-3 py-1.5 border-b border-border-primary/20 last:border-0 cursor-pointer"
        onClick={(e) => e.stopPropagation()}
      >
        <Input
          type="checkbox"
          checked={isSelected}
          onChange={() => handleTypeToggle(type.value)}
          disabled={disabled}
          className="h-4 w-4 rounded border-border-primary bg-background-form text-red-400 focus:ring-red-400 cursor-pointer"
        />
        <span className="text-sm text-text-primary">
          {type.label}
        </span>
        {isSelected && (
          <span className="ml-auto text-xs text-text-primary bg-background-invert/15 px-2 py-0.5 rounded">
            Selected
          </span>
        )}
      </label>
    );
  })}
</div>

            <div className="flex gap-2 mt-3 pt-2 border-t border-border-primary/20">
              <Button disabled={disabled} type="button" variant="ghost" size="sm" className="text-xs h-7 text-text-primary" onClick={() => handleTypeBulk(true)}>
                Select All
              </Button>
              <Button disabled={disabled} type="button" variant="ghost" size="sm" className="text-xs h-7" onClick={() => handleTypeBulk(false)}>
                Clear
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Section 4: Boolean Search Platforms ─── */}
      {booleanPlatforms.length > 0 && (
      <div>
        <h3 className="text-[16px] font-semibold text-text-primary mb-1">Boolean Search Platforms</h3>
        <p className="text-sm text-text-muted mb-4">Select the platforms to generate Boolean search strings for on this job.</p>

        <div className="bg-background-primary/30 rounded-lg p-3 border border-border-primary/50">
          <div className="grid grid-cols-2 gap-x-4 gap-y-0">
            {booleanPlatforms.map((platform) => {
              const isSelected = selectedBooleanPlatforms.includes(platform.value);
              return (
                <label
                  key={platform.value}
                  className="flex items-center gap-3 py-1.5 border-b border-border-primary/20 last:border-0 cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handlePlatformToggle(platform.value)}
                    disabled={disabled}
                    className="h-4 w-4 rounded border-border-primary bg-background-form text-red-400 focus:ring-red-400 cursor-pointer"
                  />
                  <span className="text-sm text-text-primary">{platform.label}</span>
                  {isSelected && (
                    <span className="ml-auto text-xs text-text-primary bg-background-invert/15 px-2 py-0.5 rounded">
                      Selected
                    </span>
                  )}
                </label>
              );
            })}
          </div>

          <div className="flex gap-2 mt-3 pt-2 border-t border-border-primary/20">
            <Button disabled={disabled} type="button" variant="ghost" size="sm" className="text-xs h-7 text-text-primary" onClick={() => handlePlatformBulk(true)}>
              Select All
            </Button>
            <Button disabled={disabled} type="button" variant="ghost" size="sm" className="text-xs h-7" onClick={() => handlePlatformBulk(false)}>
              Clear
            </Button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default FieldConfigSelector;
